/**
 * SecureEmbed Energy Portal - Related Pages Card
 *
 * Collapsible card grid showing related pages with navigation type indicators.
 * Addresses bug #289 — Related Pages cards must indicate navigation behaviour
 * (same-tab, new-tab, or modal) with clear icon indicators.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, ExternalLink, Maximize2,
  ChevronDown, ChevronUp, Compass,
  Search, Download, Building2, Database, Lightbulb,
  GitBranch, FileText, BookOpen,
} from 'lucide-react';
import { getRelatedPages, type RelatedPage, type NavigationType } from '../constants/relatedPages';

// Map icon names to Lucide components
const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  Search, Download, Building2, Database, Lightbulb,
  GitBranch, FileText, BookOpen, ExternalLink,
};

// Navigation type indicator icons and tooltips
const NAV_INDICATORS: Record<NavigationType, { icon: React.FC<{ className?: string }>; tooltip: string }> = {
  'same-tab': { icon: ArrowRight, tooltip: '' },
  'new-tab': { icon: ExternalLink, tooltip: 'Opens in new tab' },
  'modal': { icon: Maximize2, tooltip: 'Opens in overlay' },
};

interface RelatedPagesCardProps {
  currentRoute: string;
}

const RelatedPagesCard: React.FC<RelatedPagesCardProps> = ({ currentRoute }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const relatedPages = getRelatedPages(currentRoute);

  if (relatedPages.length === 0) return null;

  const handleClick = (page: RelatedPage) => {
    if (page.navigationType === 'new-tab' || page.external) {
      window.open(page.route, '_blank', 'noopener,noreferrer');
    } else {
      // same-tab or modal (modal renders as same-tab for now)
      const params = page.prefillParams
        ? '?' + new URLSearchParams(page.prefillParams).toString()
        : '';
      navigate(page.route + params);
    }
  };

  return (
    <div className="mt-8 bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <Compass className="w-4.5 h-4.5 text-[#2B064F]" />
          <span className="text-sm font-semibold text-gray-800">
            Related Pages
          </span>
          <span className="text-xs text-gray-400">({relatedPages.length})</span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />
        }
      </button>

      {/* Card grid */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {relatedPages.map((page) => {
              const PageIcon = ICON_MAP[page.iconName] || Database;
              const navIndicator = NAV_INDICATORS[page.navigationType];
              const NavIcon = navIndicator.icon;

              return (
                <button
                  key={page.route}
                  onClick={() => handleClick(page)}
                  className="relative flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-white text-left hover:shadow-md hover:border-gray-300 transition-all group"
                >
                  {/* Page icon */}
                  <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <PageIcon className="w-4 h-4 text-gray-600" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 group-hover:text-[#2B064F] transition-colors">
                      {page.label}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {page.description}
                    </p>
                  </div>

                  {/* Navigation type indicator — top-right corner */}
                  <div
                    className="absolute top-3 right-3 flex-shrink-0"
                    title={navIndicator.tooltip || undefined}
                  >
                    <NavIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedPagesCard;
